/**
 * Minesweeper house rules.
 *
 * The three named grids below are the ones everybody already has in their
 * fingers, and they are not arbitrary: what makes a board hard is the *density*
 * of mines, not how many there are. Beginner is one square in eight, expert one
 * in five, and that difference is the whole game — at one in eight you can
 * almost always find a safe square by looking; at one in five you regularly
 * cannot, and have to work out which of two squares is the better bet.
 *
 * The two toggles exist because the classic game has a genuinely unfair edge to
 * it, and there is more than one defensible opinion about how much of that to
 * keep. See the notes on each.
 */
/** Minesweeper is played alone. Stated so the lobby has something to read. */
export const MIN_PLAYERS = 1;
export const MAX_PLAYERS = 1;
export const MIN_WIDTH = 5;
export const MAX_WIDTH = 30;
export const MIN_HEIGHT = 5;
export const MAX_HEIGHT = 20;
export const MIN_MINES = 1;
export function cellCount(rules) {
    return rules.width * rules.height;
}
/**
 * The most mines a board can carry and still be dealable.
 *
 * The opening move's safe area cannot be mined, so it has to be subtracted:
 * nine squares with `safeOpening`, one without. Without this the deal is not
 * merely hard, it is impossible — there would be nowhere left to put the last
 * mine, and the shuffle would quietly hand back a board with fewer mines than
 * the counter promises.
 */
export function maxMines(rules) {
    return cellCount(rules) - (rules.safeOpening ? 9 : 1);
}
const BASE = {
    width: 9,
    height: 9,
    mines: 10,
    safeOpening: true,
    flagsLimited: true,
};
export const RULE_PRESETS = [
    {
        id: 'beginner',
        name: 'Beginner',
        description: 'Nine by nine, ten mines — one square in eight. Small enough to hold in your head, and forgiving ' +
            'enough that patient counting will nearly always get you out. A few minutes a board.',
        rules: { ...BASE },
    },
    {
        id: 'intermediate',
        name: 'Intermediate',
        description: 'Sixteen by sixteen, forty mines. The density barely moves but the board is four times the size, ' +
            'so you are now working several regions at once and the openings stop joining up.',
        rules: { ...BASE, width: 16, height: 16, mines: 40 },
    },
    {
        id: 'expert',
        name: 'Expert',
        description: 'Thirty by sixteen, ninety-nine mines — one square in five. At that density the board regularly ' +
            'offers you nothing certain, and finishing means picking the better of two guesses and living with it.',
        rules: { ...BASE, width: 30, height: 16, mines: 99 },
    },
];
/** The set a fresh game starts with. */
export const DEFAULT_RULES = RULE_PRESETS[0].rules;
/**
 * Coerces untrusted input into a valid ruleset. Mirrors `sanitiseRules` in the
 * other engines: anything unrecognised falls back to the default rather than
 * throwing, so a stale or hostile client can never wedge a game.
 *
 * The order matters and is the only interesting thing here. `mines` is clamped
 * last, against the board and the opening rule that have already been settled,
 * because every one of the three can invalidate it — a request for ninety-nine
 * mines is perfectly reasonable on one board and undealable on another.
 */
export function sanitiseRules(input) {
    const raw = (input ?? {});
    const rules = { ...DEFAULT_RULES };
    if (typeof raw.safeOpening === 'boolean')
        rules.safeOpening = raw.safeOpening;
    if (typeof raw.flagsLimited === 'boolean')
        rules.flagsLimited = raw.flagsLimited;
    if (typeof raw.width === 'number' && Number.isFinite(raw.width)) {
        rules.width = clamp(Math.round(raw.width), MIN_WIDTH, MAX_WIDTH);
    }
    if (typeof raw.height === 'number' && Number.isFinite(raw.height)) {
        rules.height = clamp(Math.round(raw.height), MIN_HEIGHT, MAX_HEIGHT);
    }
    const ceiling = maxMines(rules);
    if (typeof raw.mines === 'number' && Number.isFinite(raw.mines)) {
        rules.mines = clamp(Math.round(raw.mines), MIN_MINES, ceiling);
    }
    else {
        rules.mines = clamp(rules.mines, MIN_MINES, ceiling);
    }
    return rules;
}
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
//# sourceMappingURL=rules.js.map